import React, { useMemo } from 'react'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { useStoreConfig } from 'config'
import { getTrimmedUrl } from '../util'
import type { CategoryInterface, Maybe } from '@pylot-data/pylotschema'
import { ProductInterface } from '@pylot-data/pylotschema'
import { CommonMetaTags } from '../CommonMetaTags'

export type CategoryProps = {
    category: CategoryInterface
    canonicalUrl: string
    titleOverride?: string
    descriptionOverride?: string
    products?: Maybe<Array<ProductInterface>>
}

export const Category = ({
    category,
    canonicalUrl,
    titleOverride,
    descriptionOverride,
    products
}: CategoryProps): JSX.Element => {
    const config = useStoreConfig()
    const { seo, base } = config

    const titleFirst = getFirstNotEmpty([
        titleOverride,
        category?.meta_title,
        category?.name,
        seo.default.title
    ])
    const description = getFirstNotEmpty([
        descriptionOverride,
        category?.meta_description,
        seo.default.description
    ])

    let imageURL = getTrimmedUrl(products?.[0]?.small_image?.url || '')

    if (category?.display_mode === 'PAGE' && category?.image) {
        if (category?.image != '') {
            imageURL = `${base.url.baseUrl}${category?.image}`
        }
    }

    const imageContent: HTMLImageElement | null = useMemo(
        () =>
            (typeof document !== 'undefined' &&
                document.querySelector(`img[src="${imageURL}"]`)) ||
            null,
        [imageURL]
    )

    const generalData = {
        title: titleFirst,
        keyword: String(category.meta_keywords),
        description: String(description),
        canonicalUrl
    }

    const twitterData = {
        title: titleFirst,
        description: description,
        domain: seo.default.twitter?.handle,
        url: canonicalUrl,
        card: seo.default.twitter?.cardType,
        image: getTrimmedUrl(imageContent?.src || ''),
        site: seo.default.twitter?.site,
        creator: seo.default.twitter?.creator
    }

    const ogData = {
        title: titleFirst,
        description: description,
        url: canonicalUrl,
        siteName: seo.default.openGraph?.site_name,
        image: getTrimmedUrl(imageContent?.src || ''),
        imageAlt: imageContent?.alt,
        imageWidth: imageContent?.width,
        imageHeight: imageContent?.height
    }

    return (
        <CommonMetaTags
            generalData={generalData}
            twitterData={twitterData}
            ogData={ogData}
        />
    )
}
